import type { CarrierSurchargePolicy } from './carrier-surcharges'
import { ALWAYS_NON_REFUNDABLE_TAX_CODES, COUPON_OPEN_CODES } from './constants'
import { resolveDomesticGSTRate } from './consolidator-fee'
import { daysUntilDeparture, departureTiming, hoursUntilDeparture } from './departure-timing'

import {
  parseCat16CancellationPenalty,
  parseCat16ConditionalNonRefundableTaxCodes,
  parseCat16IsTicketNonRefundable,
  parseCat16NonRefundableTaxCodes,
  parseCat16PctPenaltyTiers,
  resolveCat16PctTier,
} from './fare-rules-parser'

import { roundCurrency, taxAmount } from './money'
import { resolveTicketPenalty } from './resolve-penalty'
import type { NettPaidResolution } from './resolve-statement-inputs'

import {
  resolveStructuredCancellationPenalty,
  resolveStructuredConditionalTaxCodes,
  resolveStructuredHasUnverifiablePenaltyClause,
  resolveStructuredIsNonRefundable,
  resolveStructuredIsRefundable,
  resolveStructuredIssuanceWaiver,
  resolveStructuredNonRefundableTaxCodes,
  resolveStructuredPctTiers,
} from './structured-fare-rules'

import type {
  AirlineDeductions,
  StructuredConditionalTaxCode,
  AirlineWaiverFee,
  BookingFinancialInputs,
  DeductionSource,
  DollarUncertainty,
  RefundQuoteData,
  RefundQuoteTicket,
  TicketDeduction,
} from './types'

import { classifyTicketsByPaxType } from './utils'

/**
 * The single deduction resolver: everything the airline keeps, resolved once per quote
 * — per ticket where data allows — with a tagged source. All FX conversion happens here; the
 * ledgers downstream are single-currency arithmetic. There is deliberately NO fallback that
 * derives a penalty from other refund estimates.
 */

export interface DeductionOverrides {
  /** The "airline cancellation fee" field: penalty + non-refundable taxes combined. */
  deductionsTotal?: number
  /**
   * The "supplier cash refund" field, entered as the GROSS airline refund (decision A):
   * implied deductions = NettPaid − value. Wins over deductionsTotal when both are set.
   */
  grossSupplierRefund?: number
  adminFee?: number
}

export interface ResolveDeductionsInput {
  refundQuoteData: RefundQuoteData
  booking: BookingFinancialInputs
  selectedTicketNumbers: Set<string>
  currency: string
  now?: Date
  isVoidWindow: boolean
  noTicketsIssued: boolean
  nettPaid: NettPaidResolution
  overrides?: DeductionOverrides
  /** The booking carrier's YQ/YR policy from the Ops App table; absent when the carrier isn't in it. */
  surchargePolicy?: CarrierSurchargePolicy
  /** A waiver the agent entered whose conditions are all met: its fee replaces each ticket's own. */
  waiverFee?: AirlineWaiverFee
}

/**
 * How far below the filed Cat 16/33 rule a Sabre ticket-level penalty can sit before it's flagged
 * as suspect rather than trusted outright (JWXXCB/PLT2434599: Sabre quoted $2.45 against a filed
 * ~$371/pricing-unit rule — a ~150x gap that fed straight into the supplier-refund estimate with no
 * signal anything was wrong). `resolveTicketPenalty`'s cap only ever pulls a too-HIGH Sabre figure
 * down to Cat 16 — this catches the opposite direction. 5x is deliberately generous: genuine
 * promotional/near-free fares that undercut their own boilerplate Cat 16 text are common and must
 * not be flagged.
 */
const PENALTY_DIVERGENCE_RATIO = 5

// A paid Jetstar booking is ticketless, so there are no tickets or fare rules for the ladder below
// to read and it fell to UNRESOLVED, quoting the whole nett back from an airline that refunds none
// of it. A missing carrier is read as JQ, the only LCC carrier PLT books through Sabre.
const isJetstarLcc = (rqd: RefundQuoteData): boolean =>
  Boolean(rqd.lccBooking) && (!rqd.carrier || rqd.carrier.toUpperCase() === 'JQ')

/** Ladder order — higher index = weaker source; the aggregate reports the weakest used. */
const SOURCE_ORDER: DeductionSource[] = [
  'VOID_WINDOW',
  'OPERATOR_OVERRIDE',
  'SABRE_AUTOMATED_QUOTE',
  'AIRLINE_WAIVER',
  'SABRE_TICKET_NO_FEE',
  'SABRE_TICKET_PENALTY',
  'STRUCTURED_RULES',
  'PARSED_FARE_RULES',
  'AIRLINE_POLICY',
  'NON_REFUNDABLE_FARE',
  'UNRESOLVED',
]

export function resolveAirlineDeductions(input: ResolveDeductionsInput): AirlineDeductions {
  const { refundQuoteData: rqd, booking, currency } = input
  const overrides = input.overrides ?? {}
  const warnings: string[] = []

  if (input.isVoidWindow || input.noTicketsIssued) {
    return aggregate([], 0, warnings, 'VOID_WINDOW')
  }

  const adminFee = overrides.adminFee ?? rqd.refundAdminFee ?? 0

  // 1. Operator overrides — authoritative as entered.
  if (overrides.grossSupplierRefund !== undefined) {
    const total = roundCurrency(Math.max(0, input.nettPaid.total - overrides.grossSupplierRefund))

    if (
      overrides.deductionsTotal !== undefined &&
      Math.abs(roundCurrency(overrides.deductionsTotal + adminFee) - total) > 0.01
    ) {
      warnings.push(
        `override-conflict: airline-fee override (${overrides.deductionsTotal}) disagrees with the ` +
          `deductions implied by the gross supplier refund (${total}) — using the gross figure`,
      )
    }

    // The gross figure already reflects everything the airline keeps, admin fee included.
    return {
      penalty: total,
      nonRefundableTaxes: 0,
      adminFee: 0,
      total,
      floorTaxesWithheld: 0,
      source: 'OPERATOR_OVERRIDE',
      perTicket: [],
      warnings,
      requiresManualDeductions: false,
    }
  }

  if (overrides.deductionsTotal !== undefined) {
    return {
      penalty: roundCurrency(overrides.deductionsTotal),
      nonRefundableTaxes: 0,
      adminFee: roundCurrency(adminFee),
      total: roundCurrency(overrides.deductionsTotal + adminFee),
      floorTaxesWithheld: 0,
      source: 'OPERATOR_OVERRIDE',
      perTicket: [],
      warnings,
      requiresManualDeductions: false,
    }
  }

  if (isJetstarLcc(rqd)) {
    warnings.push(
      'jetstar-non-refundable: Jetstar keeps the whole fare (Flex bundles give a credit voucher, not cash); ' +
        'only Business Max refunds cash, less $50 per passenger — enter it as an override if so',
    )
    return aggregate([], 0, warnings, 'AIRLINE_POLICY', roundCurrency(input.nettPaid.total), 0)
  }

  const tickets = rqd.tickets ?? []
  const selectedTickets = tickets.filter(t => input.selectedTicketNumbers.has(t.number))
  const timing = departureTiming(booking.departureDate, input.now)
  const fareFxRate = rqd.fareToBookingCurrencyRate ?? 1
  const penaltyFxRate = rqd.penaltyToBookingCurrencyRate ?? rqd.fareToBookingCurrencyRate

  const cat16 = resolveCat16Penalty(input, selectedTickets, timing, warnings)
  const nonRefCodes =
    resolveStructuredNonRefundableTaxCodes(rqd.structuredFareRules) ??
    parseCat16NonRefundableTaxCodes(rqd.fareRules ?? [])

  const declaredNonRefCodes = new Set(nonRefCodes)
  const withheldCodes = new Set([...ALWAYS_NON_REFUNDABLE_TAX_CODES, ...nonRefCodes])

  // Codes the fare declares non-refundable ONLY for non-refundable tickets ("FOR NON REFUNDABLE
  // TICKETS - THE YQ/YR WILL NOT BE REFUNDED"). On a refundable ticket they follow the fare and come
  // back — so the estimated (refundable-with-penalty) paths drop them from the YQ/YR floor. A code
  // the fare ALSO declares unconditionally non-refundable stays withheld (declared wins).
  const conditionalNonRefCodes = parseCat16ConditionalNonRefundableTaxCodes(rqd.fareRules ?? [])
  const refundableWithheldCodes = new Set(
    [...withheldCodes].filter(c => !conditionalNonRefCodes.has(c) || declaredNonRefCodes.has(c)),
  )
  const cat16IsNonRefundable =
    resolveStructuredIsNonRefundable(rqd.structuredFareRules) ?? parseCat16IsTicketNonRefundable(rqd.fareRules ?? [])
  // Codes an AI reading says the fare withholds only in a stated situation — checked per ticket.
  const conditionalTaxCodes = resolveStructuredConditionalTaxCodes(rqd.structuredFareRules)

  // AU 10% / NZ 15% — GST charged on the fare itself (ticketPayments.subtotal is tax-exclusive).
  // Only relevant to NON_REFUNDABLE_FARE below: forfeiting "the fare" also forfeits the GST the
  // airline charged on it, because that GST was remitted against a genuine domestic supply. A
  // stated Cat 16 cancellation FEE ("CHARGE AUD X FOR CANCEL/REFUND") is a different animal — it's
  // the airline's own final charged figure, not a raw fare component, so it needs no GST layered on.
  const domesticGstRate = resolveDomesticGSTRate(rqd.fareRules)

  if (resolveStructuredHasUnverifiablePenaltyClause(rqd.structuredFareRules)) {
    warnings.push(
      'unverifiable-penalty-clause: the filed rule gates a further reduction on unstated data — verify manually',
    )
  }

  // Booking-level fallback: no ticket data at all, but a whole-PNR penalty exists (e.g. segments
  // already cancelled before quoting). atpco.penaltyFee needs no full-selection guard here — with no
  // tickets the quote IS the whole booking.
  if (selectedTickets.length === 0) {
    const paxCount = (rqd.paxAdtCount ?? booking.travellersTotal ?? 1) + (rqd.paxChdCount ?? 0)
    const atcoTotal = rqd.atpco?.penaltyFee ? Math.round(rqd.atpco?.penaltyFee * (penaltyFxRate ?? 1)) : null
    const penalty = atcoTotal ?? (cat16.amount != null ? cat16.amount * paxCount : null)
    const nonRef = rqd.atpco?.nonRefundableTaxes
      ? Math.round(rqd.atpco?.nonRefundableTaxes * (penaltyFxRate ?? 1) * 10) / 10
      : 0

    // Resolve when ATPCO produced ANY whole-PNR figure — a penalty OR known non-refundable taxes,
    // including under an affirmative $0 penalty (a "NO CHARGE" fare that still withholds the carrier
    // surcharge). The old `penalty != null` gate discarded those taxes and fell to UNRESOLVED
    // (OZCJWX: airline kept the YQ surcharge but we quoted the entire nett back).
    if (penalty != null || nonRef > 0) {
      warnings.push('booking-level-estimate: no ticket data — derived from whole-PNR fare rules')
      return aggregate(
        [],
        adminFee,
        warnings,
        atcoTotal != null ? 'PARSED_FARE_RULES' : cat16.provenance ?? 'PARSED_FARE_RULES',
        roundCurrency(penalty ?? 0),
        nonRef,
      )
    }

    // ATPCO produced no whole-PNR figure at all. Fall back to Cat 16's own text: same precedence as
    // the per-ticket ladder below, where a confirmed non-refundable fare beats leaving the ticket
    // UNRESOLVED. Without this, a non-refundable fare with no surviving ticket objects and no ATPCO
    // penalty figure quoted the entire nett back (AEQWXF).
    if (cat16IsNonRefundable) {
      warnings.push(
        'non-refundable-fare: Cat 16 fare rules confirm the ticket is non-refundable and no ticket ' +
          'objects survive — the full nett paid is withheld',
      )
      return aggregate([], adminFee, warnings, 'NON_REFUNDABLE_FARE', roundCurrency(input.nettPaid.total), 0)
    }

    return aggregate([], 0, [...warnings, 'no-penalty-data: manual entry required'], 'UNRESOLVED')
  }

  // 2–5. Per-ticket ladder.
  const estimatedNeedingAtco: RefundQuoteTicket[] = []
  const perTicket: TicketDeduction[] = []
  const uncertainties: DollarUncertainty[] = []

  for (const t of selectedTickets) {
    perTicket.push(
      resolveTicketDeduction(t, {
        rqd,
        currency,
        timing,
        fareFxRate,
        penaltyFxRate,
        cat16,
        withheldCodes,
        refundableWithheldCodes,
        declaredNonRefCodes,
        cat16IsNonRefundable,
        conditionalTaxCodes,
        hoursBeforeDeparture: hoursUntilDeparture(booking.departureDate, input.now),
        domesticGstRate,
        surchargePolicy: input.surchargePolicy,
        waiverFee: input.waiverFee,
        nettPaid: input.nettPaid,
        selectedCount: selectedTickets.length,
        warnings,
        uncertainties,
        estimatedNeedingAtco,
      }),
    )
  }

  // 49005: Sabre's automated refunds aren't active for the ticket's PCC, so no refundTotals came
  // back and the refunded taxes above are our estimate, not the carrier's figure.
  for (const line of perTicket) {
    if (line.source === 'SABRE_AUTOMATED_QUOTE' || !refundsNotActive(rqd, line.ticketNumber)) continue
    warnings.push(
      `sabre-refunds-not-active: ticket ${line.ticketNumber} — Sabre's automated refunds are not active for it ` +
        `(49005), so the taxes it refunds are estimated`,
    )
  }

  // Whole-PNR ATPCO penalty: only usable when the selection covers the entire booking (it cannot
  // be pro-rated); split evenly across the tickets that needed it.
  if (estimatedNeedingAtco.length > 0 && rqd.atpco?.penaltyFee) {
    if (selectedTickets.length === tickets.length) {
      const atcoTotal = Math.round(rqd.atpco?.penaltyFee * (penaltyFxRate ?? 1))
      const perTicketShare = roundCurrency(atcoTotal / estimatedNeedingAtco.length)
      for (const line of perTicket) {
        if (estimatedNeedingAtco.some(t => t.number === line.ticketNumber)) {
          line.penalty = perTicketShare
          line.source = 'PARSED_FARE_RULES'
        }
      }
    } else {
      warnings.push('atco-partial-selection: whole-PNR ATPCO penalty cannot be pro-rated to a partial selection')
    }
  }

  const result = aggregate(perTicket, adminFee, warnings)
  // YQ/YR withheld only on the estimation floor: the carrier may refund it after all.
  for (const line of perTicket) {
    if (line.floorTaxes > 0) {
      uncertainties.push({
        code: 'yqyr-unconfirmed',
        ticketNumber: line.ticketNumber,
        amount: line.floorTaxes,
        direction: 'higher',
      })
    }
  }
  // The rule may take the fee down further on data we don't have: as far as nothing.
  if (resolveStructuredHasUnverifiablePenaltyClause(rqd.structuredFareRules) && result.penalty > 0) {
    uncertainties.push({ code: 'unverifiable-penalty-clause', amount: result.penalty, direction: 'higher' })
  }
  return uncertainties.length ? { ...result, uncertainties } : result
}

interface TicketDeductionContext {
  rqd: RefundQuoteData
  currency: string
  timing: ReturnType<typeof departureTiming>
  fareFxRate: number
  penaltyFxRate: number | undefined
  cat16: Cat16Resolution
  withheldCodes: Set<string>
  /** withheldCodes minus codes the fare declares non-refundable only for non-refundable tickets —
   * the floor used on refundable-with-penalty tickets (estimatedNonRefundableTaxes). */
  refundableWithheldCodes: Set<string>
  /** Only the codes the filing declares non-refundable — the YQ/YR floor excluded. */
  declaredNonRefCodes: Set<string>
  cat16IsNonRefundable: boolean
  /** Codes withheld only in a stated situation (AI readings only); see resolveConditionalTaxCodes. */
  conditionalTaxCodes: StructuredConditionalTaxCode[]
  /** Infinity when the departure date is unknown. */
  hoursBeforeDeparture: number
  /** 0.10 AU-domestic / 0.15 NZ-domestic / 0 — GST on a NON_REFUNDABLE_FARE ticket's retained fare. */
  domesticGstRate: number
  surchargePolicy?: CarrierSurchargePolicy
  waiverFee?: AirlineWaiverFee
  nettPaid: NettPaidResolution
  selectedCount: number
  warnings: string[]
  uncertainties: DollarUncertainty[]
  estimatedNeedingAtco: RefundQuoteTicket[]
}

function resolveTicketDeduction(t: RefundQuoteTicket, ctx: TicketDeductionContext): TicketDeduction {
  // A waiver sets the fee the airline charges (FJ PPAZ04926: $100 per ticket), whatever the fare rules
  // or Sabre say. Taxes follow the refundable-fare default, so YQ/YR still goes by the carrier table.
  if (ctx.waiverFee) {
    const est = estimatedNonRefundableTaxes(t.number, ctx, false, ctx.waiverFee.perTicket)
    return {
      ticketNumber: t.number,
      penalty: roundCurrency(ctx.waiverFee.perTicket),
      nonRefundableTaxes: est.nonRefundableTaxes,
      floorTaxes: est.floorTaxes,
      source: 'AIRLINE_WAIVER',
    }
  }

  // Cat 16 saying non-refundable beats any Sabre quote (matches the historical precedence).
  if (!ctx.cat16IsNonRefundable) {
    const automated = resolveAutomatedDeduction(t, ctx)
    if (automated) return automated

    // Sabre ticket-level penalty (refundPenalties/refundFee), Cat 16-capped and FX-converted.
    if (t.isRefundable === true && (t.refundPenalties || t.refundFee)) {
      const penalty = resolveTicketPenalty(t, {
        timing: ctx.timing,
        bookingCurrency: ctx.currency,
        conversionRate: ctx.penaltyFxRate,
        cat16CancellationPenalty: ctx.cat16.amount,
      })

      if (penalty != null) {
        if (ctx.cat16.amount != null && penalty > 0 && ctx.cat16.amount >= penalty * PENALTY_DIVERGENCE_RATIO) {
          ctx.warnings.push(
            `penalty-below-filed-rule: ticket ${t.number} — Sabre quoted a penalty of ${roundCurrency(penalty)} but ` +
              `the filed fare rule charges ${roundCurrency(ctx.cat16.amount)}; verify the real cancellation fee ` +
              `with the airline/consolidator before trusting the lower figure`,
          )
          ctx.uncertainties.push({
            code: 'penalty-below-filed-rule',
            ticketNumber: t.number,
            amount: roundCurrency(ctx.cat16.amount - penalty),
            direction: 'lower',
          })
        }

        // Same withholding default as every other estimated path: carrier surcharges (YQ/YR) plus
        // any fare-rule-declared codes stick; government/airport taxes on unused coupons come back.
        //
        // This path used to withhold ALL the ticket's taxes, generalised from one unconfirmed
        // fixture (INPGIE). Scored against the 10 airline-confirmed fixtures that reach it, that
        // rule under-predicted the supplier refund in 8 of 10 cases — mean error $138, median $65,
        // systematically negative. YQ/YR-only halves both ($66 / $35) and removes the bias.
        // MRLFJU is the known exception (airline kept everything); it reads +215 under this rule.
        const confirmedZero = isConfirmedZeroPenalty(t, ctx, penalty)
        const est = estimatedNonRefundableTaxes(t.number, ctx, confirmedZero, penalty)

        // A no-fee refund on the exact per-ticket payment tracked the supplier's remittance about as
        // closely as an airline quote did, so it earns its own source (SPEC.md §3, 2c).
        const source: DeductionSource =
          isNoFeeRefund(t, ctx, penalty) && ctx.nettPaid.basis === 'TICKET_PAYMENTS'
            ? 'SABRE_TICKET_NO_FEE'
            : 'SABRE_TICKET_PENALTY'

        return {
          ticketNumber: t.number,
          penalty: roundCurrency(penalty),
          nonRefundableTaxes: est.nonRefundableTaxes,
          floorTaxes: est.floorTaxes,
          source,
        }
      }
    }

    // Rules-derived penalty (structured AI extraction, else parsed Cat 16 text). A rules-resolved 0
    // is affirmative — the filing says this fare cancels free.
    if (ctx.cat16.amount != null) {
      const est = estimatedNonRefundableTaxes(t.number, ctx, ctx.cat16.amount === 0, ctx.cat16.amount)
      return {
        ticketNumber: t.number,
        penalty: roundCurrency(ctx.cat16.amount),
        nonRefundableTaxes: est.nonRefundableTaxes,
        floorTaxes: est.floorTaxes,
        source: ctx.cat16.provenance ?? 'PARSED_FARE_RULES',
      }
    }

    // Only the whole-PNR ATPCO figure remains — resolved at the aggregate level. The 0 penalty here
    // is a placeholder for "not yet known", not a free cancellation, so the YQ/YR floor still holds.
    if (ctx.rqd.atpco?.penaltyFee) {
      ctx.estimatedNeedingAtco.push(t)
      const est = estimatedNonRefundableTaxes(t.number, ctx, false, null)
      return {
        ticketNumber: t.number,
        penalty: 0,
        nonRefundableTaxes: est.nonRefundableTaxes,
        floorTaxes: est.floorTaxes,
        source: 'UNRESOLVED',
      }
    }

    // ATPCO confirms cancellation is allowed with no fee, but Sabre gave nothing for the ticket
    // (usually UNABLE_TO_CHECK_TICKET). See atpcoNoFeeBlocker for when it is not used. Scored as a
    // fare-rules estimate with a note rather than a confirmed no-fee refund.
    if (ctx.rqd.atpco?.confirmedNoFee && t.isRefundable !== false && atpcoNoFeeBlocker(t.number, ctx) == null) {
      ctx.warnings.push(
        `atpco-no-fee-unchecked: ticket ${t.number} — ATPCO confirms cancellation with no fee and every coupon ` +
          `is open, but Sabre could not price the refund; confirm the fee before processing`,
      )
      const est = estimatedNonRefundableTaxes(t.number, ctx, true, 0)
      return {
        ticketNumber: t.number,
        penalty: 0,
        nonRefundableTaxes: est.nonRefundableTaxes,
        floorTaxes: est.floorTaxes,
        source: 'PARSED_FARE_RULES',
      }
    }
  }

  // Non-refundable fare: the airline keeps the base fare; refundable taxes still come back. This
  // requires POSITIVE evidence of non-refundability from the fare rules (structured or parsed Cat
  // 16). Sabre's own `isRefundable: false` is a false-negative-prone automated-check flag and is NOT
  // sufficient on its own — trusting it wrote off the entire base fare on fares that were actually
  // refundable (ALPQEK: NZ flexi Business fare, rules say FULL REFUND, airline refunded 100%;
  // DQXVHI: no filed rules at all, airline refunded). Those fall through to UNRESOLVED, below.
  if (ctx.cat16IsNonRefundable) {
    // The airline keeps the base fare — and, on an AU/NZ-domestic fare, the GST it charged on that
    // fare too (HONIQH/PLT2334919: ticketPayments.subtotal is tax-exclusive; forfeiting "the fare"
    // forfeits the GST remitted against it, since the retained portion is still a taxable supply).
    // WG/QR-style government taxes on the unused/refunded portion are unaffected by this.
    const penalty = ticketBaseFare(t.number, ctx)
    const gstOnRetainedFare = roundCurrency(ctx.domesticGstRate * penalty)
    const conditional = resolveConditionalTaxCodes(t.number, ctx, penalty, true)
    const withheldCodes = applyConditionalTaxCodes(ctx.withheldCodes, ctx.declaredNonRefCodes, conditional)
    if (TRAVEL_CREDIT_CARRIER_PREFIXES.has(t.number.slice(0, 3))) {
      ctx.warnings.push(
        `travel-credit-likely: ticket ${t.number} — a non-refundable AA, DL or UA fare usually comes back as a ` +
          `travel credit for the passenger, not cash, so the taxes quoted here may never be paid. Confirm before processing.`,
      )
      // The taxes that would come back in cash; a credit returns none of it.
      const cash = ticketTaxSum(t.number, ctx, code => !withheldCodes.has(code))
      if (cash > 0) {
        ctx.uncertainties.push({
          code: 'travel-credit-likely',
          ticketNumber: t.number,
          amount: cash,
          direction: 'lower',
        })
      }
    }
    return {
      ticketNumber: t.number,
      penalty,
      nonRefundableTaxes: roundCurrency(
        ticketTaxSum(t.number, ctx, code => withheldCodes.has(code)) + gstOnRetainedFare,
      ),
      floorTaxes: floorTaxAmount(
        t.number,
        ctx,
        withheldCodes,
        withConditionalTaxCodes(ctx.declaredNonRefCodes, conditional),
      ),
      source: 'NON_REFUNDABLE_FARE',
    }
  }

  // Safety double-check for the Sabre-vs-rules conflict: Sabre flagged the ticket non-refundable but
  // the fare rules don't confirm it and no penalty could be computed. Surface it loudly (→ manual
  // entry) rather than silently writing off the fare (the old bug) or silently refunding everything.
  if (t.isRefundable === false) {
    ctx.warnings.push(
      `refundability-conflict: ticket ${t.number} — Sabre flags it non-refundable but the fare rules ` +
        `do not confirm non-refundability and no cancellation penalty could be computed. Left UNRESOLVED ` +
        `for manual entry — verify the refundable amount with the consolidator before quoting.`,
    )
  } else {
    // Says what was missing, so a stored "none" quote can be traced without re-running it.
    const sabre = t.isRefundable === true ? 'Sabre marks it refundable but gave no penalty' : 'Sabre could not check it'
    const atpco = ctx.rqd.atpco?.confirmedNoFee
      ? `ATPCO no fee, but ${atpcoNoFeeBlocker(t.number, ctx)}`
      : ctx.rqd.atpco?.penaltyFee === 0
        ? 'ATPCO fee 0 not confirmed as free'
        : 'no ATPCO figure'
    ctx.warnings.push(`unresolved-ticket: ticket ${t.number} — ${sabre}, no fare-rule fee, ${atpco}`)
  }

  return { ticketNumber: t.number, penalty: 0, nonRefundableTaxes: 0, floorTaxes: 0, source: 'UNRESOLVED' }
}

/**
 * SABRE_AUTOMATED_QUOTE: implied per-ticket deduction = ticketNett − (refundTotals.total +
 * feeOvercharge). The overcharge correction adds back Sabre's per-fare-component double-charge
 * of a per-ticket Cat 16 fee, only for carriers whose refunds show they take the fee once (NVXAZC).
 * Requires a per-ticket nett basis — PD_NET_FARE_COST is a booking-level approximation polluted
 * by commission and would corrupt the implied figure.
 */
function resolveAutomatedDeduction(t: RefundQuoteTicket, ctx: TicketDeductionContext): TicketDeduction | null {
  if (t.isRefundable !== true || !t.refundTotals) return null

  if (t.refundTotals.currencyCode !== ctx.currency) {
    ctx.warnings.push(
      `foreign-refund-totals: ticket ${t.number} quoted in ${t.refundTotals.currencyCode} — falling back to fare rules`,
    )
    return null
  }

  const ticketNett = ctx.nettPaid.perTicket[t.number]
  if (ticketNett == null || ticketNett <= 0) {
    ctx.warnings.push(
      `no-per-ticket-nett: ticket ${t.number} has an automated quote but no per-ticket nett basis ` +
        `(${ctx.nettPaid.basis}) — falling back to fare rules`,
    )
    return null
  }

  const fees = parseFloat(t.refundTotals.fees) || 0
  // The overcharge correction exists for Sabre charging a per-TICKET Cat 16 fee once per fare
  // component (fees ≈ n × cat16, n small). A cat16 below half the fee is not a plausible cap —
  // the two figures aren't in the same currency reality — so leave Sabre's own numbers alone.
  const capsAtCat16 =
    ctx.cat16.amount != null &&
    ctx.cat16.amount * 2 >= fees &&
    FEE_CHARGED_ONCE_CARRIER_PREFIXES.has(t.number.slice(0, 3))
  const feeOvercharge = capsAtCat16 ? Math.max(0, fees - ctx.cat16.amount!) : 0
  const refunded = (parseFloat(t.refundTotals.total) || 0) + feeOvercharge
  const implied = roundCurrency(ticketNett - refunded)

  if (implied < -0.01) {
    ctx.warnings.push(
      `implied-deduction-negative: ticket ${t.number} refunds ${refunded} against nett ${ticketNett} — clamped to 0`,
    )
    return { ticketNumber: t.number, penalty: 0, nonRefundableTaxes: 0, floorTaxes: 0, source: 'SABRE_AUTOMATED_QUOTE' }
  }

  // Display split only — the TOTAL is authoritative.
  const cappedFees = capsAtCat16 ? Math.min(fees, ctx.cat16.amount!) : fees
  const penalty = Math.min(implied, roundCurrency(cappedFees))
  return {
    ticketNumber: t.number,
    penalty,
    nonRefundableTaxes: roundCurrency(implied - penalty),
    // Automated path trusts Sabre's refundTotals verbatim — the YQ/YR floor is never applied here.
    floorTaxes: 0,
    source: 'SABRE_AUTOMATED_QUOTE',
  }
}

interface Cat16Resolution {
  amount: number | null
  provenance: Extract<DeductionSource, 'STRUCTURED_RULES' | 'PARSED_FARE_RULES'> | null
}

/**
 * Cat 16/31/33 penalty with provenance: structured (AI-extracted) rules win — issuance waiver,
 * then timing-scoped fixed penalty, then pct tiers — falling back to the regex parsers.
 *
 * FX confidence: a penalty filed in a foreign currency converts with the DEDICATED
 * penaltyToBookingCurrencyRate. The fare rate is only borrowed when that's absent, and a
 * borrowed-rate conversion is DISCARDED when ATPCO's own converted figure contradicts it —
 * the penalty's filing currency need not be the fare's (HEUEPQ: EUR 250 penalty on a MAD-filed
 * fare came out as 38 AUD via the MAD rate; Sabre's real figure was 406).
 */
function resolveCat16Penalty(
  input: ResolveDeductionsInput,
  selectedTickets: RefundQuoteTicket[],
  timing: ReturnType<typeof departureTiming>,
  warnings: string[],
): Cat16Resolution {
  const rqd = input.refundQuoteData
  const structured = rqd.structuredFareRules
  const hoursBeforeDeparture = hoursUntilDeparture(input.booking.departureDate, input.now)
  const hoursSinceIssuance = rqd.bookingCreatedAt
    ? ((input.now ?? new Date()).getTime() - new Date(rqd.bookingCreatedAt).getTime()) / (1000 * 60 * 60)
    : undefined

  const resolveWithRate = (rate: number | undefined): Cat16Resolution => {
    const structuredFixed =
      resolveStructuredIssuanceWaiver(structured, input.currency, rate, hoursSinceIssuance) ??
      resolveStructuredCancellationPenalty(structured, input.currency, rate, timing, hoursBeforeDeparture)
    if (structuredFixed != null) return { amount: structuredFixed, provenance: 'STRUCTURED_RULES' }

    const parsedFixed = parseCat16CancellationPenalty(rqd.fareRules ?? [], input.currency, rate, timing)
    if (parsedFixed != null) return { amount: parsedFixed, provenance: 'PARSED_FARE_RULES' }

    const structuredTiers = resolveStructuredPctTiers(structured)
    const tiers = structuredTiers.length > 0 ? structuredTiers : parseCat16PctPenaltyTiers(rqd.fareRules ?? [])
    const tier = resolveCat16PctTier(tiers, daysUntilDeparture(input.booking.departureDate, input.now))

    if (tier != null) {
      const fareBase = pctFareBase(rqd, input.selectedTicketNumbers)
      return {
        amount: Math.ceil((fareBase * tier.pct) / 100),
        provenance: structuredTiers.length > 0 ? 'STRUCTURED_RULES' : 'PARSED_FARE_RULES',
      }
    }

    // Rules affirm refundability but no fee resolved for this timing: free — but only when Sabre
    // quoted nothing, a filed 0 and an unknown 0 are indistinguishable downstream.
    const sabreQuotedSomething = selectedTickets.some(
      t => t.isRefundable === true && (t.refundTotals != null || t.refundPenalties != null),
    )
    if (!sabreQuotedSomething && resolveStructuredIsRefundable(structured) === true) {
      return { amount: 0, provenance: 'STRUCTURED_RULES' }
    }

    return { amount: null, provenance: null }
  }

  // Same-currency (or dedicated-rate) resolution is authoritative.
  const strict = resolveWithRate(rqd.penaltyToBookingCurrencyRate)
  if (strict.amount != null || rqd.penaltyToBookingCurrencyRate != null || rqd.fareToBookingCurrencyRate == null) {
    if (strict.amount == null) warnUnconvertibleCat16Penalty(rqd, timing, warnings)
    return strict
  }

  // Borrowed fare-rate conversion — accept only when ATPCO doesn't contradict it.
  const borrowed = resolveWithRate(rqd.fareToBookingCurrencyRate)
  if (borrowed.amount != null && borrowed.amount > 0 && rqd.atpco?.penaltyFee) {
    const ratio = rqd.atpco?.penaltyFee / borrowed.amount
    if (ratio > 2 || ratio < 0.5) {
      warnings.push(
        `cat16-fx-suspect: text penalty converted with the fare FX rate gives ${borrowed.amount} but ATPCO ` +
          `computed ${rqd.atpco?.penaltyFee} — penalty may be filed in a different currency than the fare; discarded`,
      )
      return { amount: null, provenance: null }
    }
  }
  return borrowed
}

/**
 * A penalty filed in a foreign currency with no usable rate parses fine but converts to nothing.
 * A null penalty resolves no cancellation fee, so an `isRefundable: false` ticket falls through to
 * UNRESOLVED (manual entry) instead of a real deduction — the ticket's true penalty silently
 * vanishes. Spec §3 requires a warning here so ops knows a penalty WAS filed and simply could not be
 * converted (HEUEPQ: EUR 250 left unconverted resolves to nothing instead of the real ~410.00).
 */
function warnUnconvertibleCat16Penalty(
  rqd: RefundQuoteData,
  timing: ReturnType<typeof departureTiming>,
  warnings: string[],
): void {
  // Re-parse with no booking currency — applyAmount then takes any filed currency verbatim, so a
  // non-null result means a penalty IS filed and we simply could not convert it. Genuinely
  // penalty-free rules return null both ways and stay quiet.
  const unconverted = parseCat16CancellationPenalty(rqd.fareRules ?? [], undefined, undefined, timing)
  if (unconverted == null) return

  warnings.push(
    `cat16-penalty-unconvertible: fare rules file a cancellation penalty of ${unconverted} in a currency ` +
      `other than the booking currency and no conversion rate is available — the penalty was discarded ` +
      `and deductions fell back to a weaker source. Enter the penalty manually before processing.`,
  )
}

function pctFareBase(rqd: RefundQuoteData, selectedTicketNumbers: Set<string>): number {
  const payments = rqd.ticketPayments
  if (payments) {
    const totals = [...selectedTicketNumbers]
      .map(n => payments[n]?.total)
      .filter((t): t is number => t != null && t > 0)
    if (totals.length > 0) return totals.reduce((a, b) => a + b, 0) / totals.length
  }
  return (rqd.netFarePerAdt ?? 0) * (rqd.fareToBookingCurrencyRate ?? 1)
}

/** Sum a ticket's taxes (booking currency) filtered by tax code. */
function ticketTaxSum(ticketNumber: string, ctx: TicketDeductionContext, keep: (code: string) => boolean): number {
  let total = 0
  for (const tax of ctx.rqd.allTaxesByTicket?.[ticketNumber] ?? []) {
    if (keep(tax.taxCode.toUpperCase())) total += taxAmount(tax, ctx.currency, ctx.fareFxRate)
  }
  return roundCurrency(total)
}

/**
 * Validating-carrier ticket prefixes (AA 001, DL 006, UA 016) whose non-refundable fares returned a travel
 * credit, not cash, in 4 of 4 fact-checked cancellations (SPEC.md §10.3). Scoring only: amounts are unchanged.
 */
const TRAVEL_CREDIT_CARRIER_PREFIXES = new Set(['001', '006', '016'])

/**
 * Validating-carrier ticket prefixes whose airline takes a per-ticket Cat 16 fee once, although
 * Sabre's automated quote charges it per fare component: Fiji Airways 260 (NVXAZC, airline-confirmed).
 * Every other carrier keeps Sabre's fee — T'way files 220 per one way and Sabre quoted 440 (FTYBUN).
 */
const FEE_CHARGED_ONCE_CARRIER_PREFIXES = new Set(['260'])

/**
 * A 0 that the data affirms — the fare cancels free — rather than a 0 standing in for an
 * unresolved penalty. Sabre files both: a contracted refundFee of 0 is a statement, while a 0
 * refundPenalty carrying conditionsApply means the real figure is gated on data we don't have
 * (see resolveTicketPenalty). Only the affirmative kind lifts the YQ/YR withholding floor.
 */
function isConfirmedZeroPenalty(t: RefundQuoteTicket, ctx: TicketDeductionContext, penalty: number): boolean {
  if (penalty !== 0) return false

  if (t.refundFee?.currencyCode === ctx.currency) return (parseFloat(t.refundFee.amount) || 0) === 0

  const filed = t.refundPenalties?.find(rp => rp.applicability === ctx.timing)
  if (!filed?.penalty) return false
  return (parseFloat(filed.penalty.amount) || 0) === 0 && !filed.conditionsApply
}

/**
 * Trusts a 0 penalty for scoring more widely than isConfirmedZeroPenalty does for amounts: a filed 0
 * that carries conditionsApply still matched the consolidator's net cash on 37 of 38 AA/DL/B6 refunds
 * (SPEC.md §10.2), so it scores as no-fee while its YQ/YR stay withheld.
 */
function isNoFeeRefund(t: RefundQuoteTicket, ctx: TicketDeductionContext, penalty: number): boolean {
  if (isConfirmedZeroPenalty(t, ctx, penalty)) return true
  if (penalty !== 0 || t.refundFee?.currencyCode === ctx.currency) return false

  const filed = t.refundPenalties?.find(rp => rp.applicability === ctx.timing)
  return filed?.penalty !== undefined && (parseFloat(filed.penalty.amount) || 0) === 0
}

/**
 * Non-refundable taxes for a rules-estimated ticket: the declared codes ∪ YQ/YR when a breakdown
 * exists (estimation default — the automated path instead trusts Sabre's actual figure); the
 * ATPCO calculator's total, tax-rounded, when it doesn't.
 *
 * YQ/YR are carrier-imposed surcharges, not government taxes: they follow the fare's own refund
 * conditions. On a confirmed 0 penalty there is nothing for them to follow, so the floor lifts and
 * only codes the filing explicitly declares non-refundable stay withheld.
 */
interface EstimatedTaxes {
  nonRefundableTaxes: number
  /** Subset of nonRefundableTaxes withheld only on the YQ/YR floor; 0 when no code breakdown exists. */
  floorTaxes: number
}

/**
 * Why ATPCO's confirmed no fee cannot resolve this ticket, or null when it can. Every coupon must be
 * open per the booking read (unknown counts as not open), so a flown or exchanged ticket is never
 * quoted as a free refund. ATPCO's non-refundable tax total is whole-booking and not code-attributed,
 * so it cannot be matched to the ticket: any such total leaves the ticket for manual entry.
 */
function atpcoNoFeeBlocker(ticketNumber: string, ctx: TicketDeductionContext): string | null {
  const coupons = ctx.rqd.ticketStatusByTicket?.[ticketNumber]?.coupons ?? []
  if (coupons.length === 0 || !coupons.every(c => c.code != null && COUPON_OPEN_CODES.has(c.code))) {
    return 'not every coupon is open'
  }

  if (ctx.rqd.atpco?.nonRefundableTaxes) {
    return 'it withholds taxes that cannot be matched to the ticket'
  }

  return null
}

function estimatedNonRefundableTaxes(
  ticketNumber: string,
  ctx: TicketDeductionContext,
  isConfirmedFullyRefundable: boolean,
  penalty: number | null,
): EstimatedTaxes {
  // Estimated (refundable-with-penalty) paths only — the NON_REFUNDABLE_FARE branch withholds the
  // full floor directly. So use refundableWithheldCodes, which drops carrier surcharges the fare
  // marks non-refundable only for NON-refundable tickets (HIINNW / MU: YQ comes back here).
  // A carrier in the Ops table settles the floor either way, ahead of the confirmed-0 heuristic,
  // and leaves no YQ/YR uncertainty behind; codes the fare declares still stick. A conditional code
  // whose situation applies (or cannot be checked) counts as declared; one whose situation does not
  // apply comes back, YQ/YR floor included.
  const conditional = resolveConditionalTaxCodes(ticketNumber, ctx, penalty, false)
  const declared = withConditionalTaxCodes(ctx.declaredNonRefCodes, conditional)
  const policyCodes =
    ctx.surchargePolicy === 'refunds'
      ? withoutFloorCodes(ctx.refundableWithheldCodes, declared)
      : ctx.surchargePolicy === 'withholds'
        ? ctx.refundableWithheldCodes
        : isConfirmedFullyRefundable
          ? ctx.declaredNonRefCodes
          : ctx.refundableWithheldCodes
  const codes = applyConditionalTaxCodes(policyCodes, ctx.declaredNonRefCodes, conditional)
  const breakdown = ctx.rqd.allTaxesByTicket?.[ticketNumber]
  if (breakdown && breakdown.length > 0) {
    return {
      nonRefundableTaxes: ticketTaxSum(ticketNumber, ctx, code => codes.has(code)),
      floorTaxes: ctx.surchargePolicy ? 0 : floorTaxAmount(ticketNumber, ctx, codes, declared),
    }
  }
  if (ctx.rqd.atpco?.nonRefundableTaxes) {
    const converted = Math.round(ctx.rqd.atpco?.nonRefundableTaxes * (ctx.penaltyFxRate ?? 1) * 10) / 10
    // ATPCO's total isn't code-attributed, so the YQ/YR share can't be isolated.
    return { nonRefundableTaxes: roundCurrency(converted / Math.max(1, ctx.selectedCount)), floorTaxes: 0 }
  }
  return { nonRefundableTaxes: 0, floorTaxes: 0 }
}

/**
 * The YQ/YR-floor share of a ticket's withheld taxes: codes withheld ONLY because of
 * ALWAYS_NON_REFUNDABLE_TAX_CODES, minus any the fare itself declares non-refundable (those stick
 * regardless of the floor). `codesUsed` is the set actually applied to this ticket — when it's the
 * declared-only set (a confirmed 0 penalty lifted the floor), this is 0.
 */
function floorTaxAmount(
  ticketNumber: string,
  ctx: TicketDeductionContext,
  codesUsed: Set<string>,
  declared: Set<string> = ctx.declaredNonRefCodes,
): number {
  return ticketTaxSum(
    ticketNumber,
    ctx,
    code => codesUsed.has(code) && ALWAYS_NON_REFUNDABLE_TAX_CODES.has(code) && !declared.has(code),
  )
}

interface ConditionalTaxCodes {
  /** Codes whose situation applies, or could not be checked: withheld like a declared code. */
  withheld: Set<string>
  /** Codes whose situation does not apply: refunded, even off the YQ/YR floor. */
  released: Set<string>
}

const CONDITION_LABELS: Record<StructuredConditionalTaxCode['condition'], string> = {
  NON_REFUNDABLE_TICKET: 'the ticket is non-refundable',
  BASE_FARE_NOT_ABOVE_PENALTY: 'the base fare is no more than the penalty',
  TICKET_PARTLY_USED: 'the ticket is partly used',
  WITHIN_HOURS_OF_DEPARTURE: 'cancelled close to departure',
  NO_SHOW: 'the passenger was a no-show',
  OTHER: 'a condition in the fare rules is met',
}

/**
 * Decides, for one ticket, each tax code an AI reading says the fare withholds only in a stated
 * situation (TS 2770487: YQ/YR kept only within 24 h or on a no-show; EK 2842426: YR kept only on a
 * partly used ticket). A code is withheld when any of its situations applies. When none applies but
 * one cannot be checked, it is withheld and flagged 'unverifiable-penalty-clause', which costs the
 * same points as the AI's own flag.
 */
function resolveConditionalTaxCodes(
  ticketNumber: string,
  ctx: TicketDeductionContext,
  penalty: number | null,
  isNonRefundable: boolean,
): ConditionalTaxCodes {
  const withheld = new Set<string>()
  const unchecked = new Map<string, string>()
  const released = new Set<string>()

  for (const entry of ctx.conditionalTaxCodes) {
    if (ctx.declaredNonRefCodes.has(entry.code)) continue
    const applies = conditionApplies(entry, ticketNumber, ctx, penalty, isNonRefundable)
    if (applies === true) withheld.add(entry.code)
    else if (applies === false) released.add(entry.code)
    else if (!unchecked.has(entry.code)) unchecked.set(entry.code, CONDITION_LABELS[entry.condition])
  }

  for (const [code, label] of unchecked) {
    if (withheld.has(code)) continue
    withheld.add(code)
    ctx.warnings.push(
      `unverifiable-penalty-clause: ticket ${ticketNumber} — the fare withholds ${code} only when ${label}, ` +
        `which the booking data cannot confirm; withheld — verify manually`,
    )
    const amount = ticketTaxSum(ticketNumber, ctx, c => c === code)
    if (amount > 0) {
      ctx.uncertainties.push({ code: 'conditional-tax-code', ticketNumber, taxCode: code, amount, direction: 'higher' })
    }
  }

  return { withheld, released: new Set([...released].filter(code => !withheld.has(code) && !unchecked.has(code))) }
}

/** true / false when the booking data settles the situation, null when it cannot. */
function conditionApplies(
  entry: StructuredConditionalTaxCode,
  ticketNumber: string,
  ctx: TicketDeductionContext,
  penalty: number | null,
  isNonRefundable: boolean,
): boolean | null {
  switch (entry.condition) {
    case 'NON_REFUNDABLE_TICKET':
      return isNonRefundable
    case 'BASE_FARE_NOT_ABOVE_PENALTY': {
      const baseFare = ticketBaseFare(ticketNumber, ctx)
      return penalty == null || baseFare <= 0 ? null : baseFare <= penalty
    }
    case 'TICKET_PARTLY_USED': {
      // No coupon can be used before the first departure.
      if (ctx.timing === 'BEFORE_DEPARTURE') return false
      const coupons = ctx.rqd.ticketStatusByTicket?.[ticketNumber]?.coupons ?? []
      if (coupons.length === 0 || coupons.some(c => c.code == null)) return null
      return !coupons.every(c => COUPON_OPEN_CODES.has(c.code!))
    }
    case 'WITHIN_HOURS_OF_DEPARTURE':
      if (entry.hours == null || !Number.isFinite(ctx.hoursBeforeDeparture)) return null
      return ctx.hoursBeforeDeparture < entry.hours
    case 'NO_SHOW':
      // A cancellation before departure cannot be a no-show; after it, nothing records whether they flew.
      return ctx.timing === 'BEFORE_DEPARTURE' ? false : null
    case 'OTHER':
      return null
  }
}

/** `declared` plus the conditional codes withheld for this ticket. */
function withConditionalTaxCodes(declared: Set<string>, conditional: ConditionalTaxCodes): Set<string> {
  return new Set([...declared, ...conditional.withheld])
}

/** `codes` with this ticket's withheld conditional codes added and released ones removed (declared codes stay). */
function applyConditionalTaxCodes(
  codes: Set<string>,
  declared: Set<string>,
  conditional: ConditionalTaxCodes,
): Set<string> {
  const result = new Set([...codes, ...conditional.withheld])
  for (const code of conditional.released) if (!declared.has(code)) result.delete(code)
  return result
}

/** Sabre answered this ticket's check with 49005, "Automated Refunds not active". */
function refundsNotActive(rqd: RefundQuoteData, ticketNumber: string): boolean {
  return (rqd.errors ?? []).some(e => e.fieldValue === ticketNumber && /\b49005\b/.test(e.description ?? ''))
}

/** `codes` without the YQ/YR floor, keeping any surcharge code the fare itself declares non-refundable. */
function withoutFloorCodes(codes: Set<string>, declared: Set<string>): Set<string> {
  return new Set([...codes].filter(code => !ALWAYS_NON_REFUNDABLE_TAX_CODES.has(code) || declared.has(code)))
}

/** Base fare (tax-exclusive) for a NON_REFUNDABLE ticket — the amount the airline keeps. */
function ticketBaseFare(ticketNumber: string, ctx: TicketDeductionContext): number {
  const payment = ctx.rqd.ticketPayments?.[ticketNumber]
  if (payment && payment.subtotal > 0) return roundCurrency(payment.subtotal)

  const tickets = ctx.rqd.tickets ?? []
  const { adultNums, childNums } = classifyTicketsByPaxType(
    tickets,
    ctx.rqd.paxAdtCount ?? tickets.length,
    ctx.rqd.paxChdCount ?? 0,
  )

  const adtFare = (ctx.rqd.netFarePerAdt ?? 0) * ctx.fareFxRate
  const chdFare = (ctx.rqd.netFarePerChd ?? 0) > 0 ? ctx.rqd.netFarePerChd! * ctx.fareFxRate : adtFare
  const infFare = (ctx.rqd.netFarePerInf ?? 0) * ctx.fareFxRate

  return roundCurrency(childNums.has(ticketNumber) ? chdFare : adultNums.has(ticketNumber) ? adtFare : infFare)
}

function aggregate(
  perTicket: TicketDeduction[],
  adminFee: number,
  warnings: string[],
  forcedSource?: DeductionSource,
  bookingLevelPenalty = 0,
  bookingLevelNonRef = 0,
): AirlineDeductions {
  const penalty = roundCurrency(perTicket.reduce((sum, d) => sum + d.penalty, 0) + bookingLevelPenalty)
  const nonRefundableTaxes = roundCurrency(
    perTicket.reduce((sum, d) => sum + d.nonRefundableTaxes, 0) + bookingLevelNonRef,
  )
  // Diagnostic subset — the YQ/YR-floor share of the taxes above. Booking-level fallbacks carry no
  // per-ticket data, so their (non-code-attributed) taxes contribute 0.
  const floorTaxesWithheld = roundCurrency(perTicket.reduce((sum, d) => sum + d.floorTaxes, 0))

  const source =
    forcedSource ??
    perTicket.reduce<DeductionSource>(
      (weakest, d) => (SOURCE_ORDER.indexOf(d.source) > SOURCE_ORDER.indexOf(weakest) ? d.source : weakest),
      'SABRE_AUTOMATED_QUOTE',
    )

  const requiresManualDeductions = source === 'UNRESOLVED' || perTicket.some(d => d.source === 'UNRESOLVED')

  // Admin-fee folding: an automated Sabre quote already reflects everything the airline
  // keeps; the fee applies only when some ticket resolves from rules. Void and unresolved carry none.
  const allAutomated = perTicket.length > 0 && perTicket.every(d => d.source === 'SABRE_AUTOMATED_QUOTE')
  const effectiveAdminFee =
    source === 'VOID_WINDOW' || source === 'UNRESOLVED' || allAutomated ? 0 : roundCurrency(adminFee)

  return {
    penalty,
    nonRefundableTaxes,
    adminFee: effectiveAdminFee,
    total: roundCurrency(penalty + nonRefundableTaxes + effectiveAdminFee),
    floorTaxesWithheld,
    source,
    perTicket,
    warnings,
    requiresManualDeductions,
  }
}
